import React, { FC, useEffect, useRef, useState } from 'react';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Form } from '@components/atoms/Form';
import { useTranslation } from 'react-i18next';
import { useData } from '@hooks/useData';
import { useGetAllGeoDistricts } from '@services/useGetAllGeoDistricts';
import { useGetAllGeoMunicipalitiesOfDistrict } from '@services/useGetAllGeoMunicipalitiesOfDistrict';
import { GetMyAccountAddressesCreateInput, useGetMyAccountAddressesCreate } from '@services/useGetMyAccountAddressesCreate';
import { InputErrors } from '@types';
import { Typography } from '@components/atoms/Typography';
import { Select, SelectType } from '@components/atoms/Select';
import { Button, ButtonMode } from '@components/atoms/Button';
import { useNotifications } from '@hooks/useNotifications';
import { useGetMyAccountAddresses } from '@services/useGetMyAccountAddresses';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { Space } from '@components/atoms/Space';
import { removeNonLetters } from '@utils/format';

interface NewAddressFormErrors {
    general: string;
    name: InputErrors;
    lastName: InputErrors;
    address: InputErrors;
    complement: InputErrors;
    district: InputErrors;
    municipality: InputErrors;
    postalCode: InputErrors;
    region: InputErrors;
    otherRegion: InputErrors;
}

interface NewAddressFormProps {
    type: string;
}

export const NewAddressForm: FC<NewAddressFormProps> = ({ type }) => {
    const { t } = useTranslation();
    const { allSettings, allGeoDistricts } = useData();
    const { request: requestGetAllGeoDistricts, loading: loadingGetAllGeoDistricts } = useGetAllGeoDistricts();
    const { request: requestGetAllGeoMunicipalities, loading: loadingGetAllGeoMunicipalities } =
        useGetAllGeoMunicipalitiesOfDistrict();
    const {
        request: requestGetMyAccountAddressesCreate,
        errorMessage: errorMessageCreate,
        validationErrors: validationErrorsCreate,
        success: successMessageCreate,
        lastResponseTime,
    } = useGetMyAccountAddressesCreate();
    const { request: requestGetMyAccountAddresses } = useGetMyAccountAddresses();
    const { showDefaultNotification } = useNotifications();

    const translations = {
        title: t('new-address-form-component.page.create-title'),
        nameLabel: t('new-address-form-component.name.label'),
        lastNameLabel: t('new-address-form-component.last-name.label'),
        fiscalNumberLabel: t('new-address-form-component.fiscal-number.label'),
        addressLabel: t('new-address-form-component.address.label'),
        complementLabel: t('new-address-form-component.complement.label'),
        postalCodeLabel: t('new-address-form-component.postal-code.label'),
        districtLabel: t('new-address-form-component.district.label'),
        selectDistrict: t('new-address-form-component.district.select'),
        regionLabel: t('new-address-form-component.region.label'),
        nameRequiredError: t('new-address-form-component.name.required-error'),
        complementRequiredError: t('new-address-form-component.complement.required-error'),
        lastNameRequiredError: t('new-address-form-component.last-name.required-error'),
        addressRequiredError: t('new-address-form-component.address.required-error'),
        districtRequiredError: t('new-address-form-component.district.required-error'),
        municipalityRequiredError: t('new-address-form-component.municipality.required-error'),
        postalCodeRequiredError: t('new-address-form-component.postal-code.required-error'),
        regionRequiredError: t('new-address-form-component.region.required-error'),
        regionOtherOption: t('new-address-form-component.region.other.option'),
        otherRegion: t('new-address-form-component.other-region.label'),
        otherRegionRequiredError: t('new-address-form-component.other-region.required-error'),
        default: t('select-default-option'),
    };

    const formRef = useRef<HTMLFormElement>(null);
    const [fieldName, setFieldName] = useState<string>('');
    const [fieldLastName, setFieldLastName] = useState<string>('');
    const [fieldFiscalNumber, setFieldFiscalNumber] = useState<string>('');
    const [fieldType] = useState<string>(type);
    const [fieldAddress, setFieldAddress] = useState<string>('');
    const [fieldComplement, setFieldComplement] = useState<string>('');
    const [fieldDistrict, setFieldDistrict] = useState<string>('');
    const [fieldMunicipality, setFieldMunicipality] = useState<string>('');
    const [fieldPostalCode, setFieldPostalCode] = useState<string>('');
    const [fieldRegion, setFieldRegion] = useState<string>('');
    const [fieldOtherRegion, setFieldOtherRegion] = useState<string>('');
    const [fieldPrimary] = useState<string>('0');
    const [disabled, setDisabled] = useState<boolean>(false);

    // districts are filtered by selected region
    const [filteredDistricts, setFilteredDistricts] = useState<{ name?: string; district_code?: string; region_uuid?: string }[]>(
        []
    );
    const [districtAndMunicipalityDisabled, setDistrictAndMunicipalityDisabled] = useState(true);

    const [errors, setErrors] = useState<NewAddressFormErrors>({
        general: '',
        name: [],
        lastName: [],
        address: [],
        complement: [],
        district: [],
        municipality: [],
        postalCode: [],
        region: [],
        otherRegion: [],
    });

    useEffect(() => {
        requestGetAllGeoDistricts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (fieldDistrict) {
            requestGetAllGeoMunicipalities({ district_code: fieldDistrict });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldDistrict]);

    // make city options depend on region
    useEffect(() => {
        if (allGeoDistricts?.data) {
            const visibleDistricts = allGeoDistricts.data?.filter((district) => district.region_uuid === fieldRegion);

            setFilteredDistricts(visibleDistricts);
            const hasVisibleDistricts = visibleDistricts.length > 0;

            setDistrictAndMunicipalityDisabled(!hasVisibleDistricts);

            // reset selection if current selection is not valid
            if (fieldDistrict && !visibleDistricts.find((district) => district.district_code === fieldDistrict)) {
                setFieldDistrict('');
                setFieldMunicipality('');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldRegion, allGeoDistricts?.data]);

    const handleFiscalInput = (value: string) => {
        value = value.replace(/[^0-9]/g, '');

        if (value.length > 9) {
            value = value.slice(0, 8);
        }

        setFieldFiscalNumber(value);
    };

    const handleNameInput = (value: string) => {
        value = removeNonLetters(value);

        setFieldName(value);
    };

    const handleLastNameInput = (value: string) => {
        value = removeNonLetters(value);

        setFieldLastName(value);
    };

    // prevent user from submitting the form multiple times with the "other" region
    useEffect(() => {
        if (fieldRegion === 'other' && fieldOtherRegion && errors.otherRegion.length > 0) {
            setDisabled(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldRegion, errors.otherRegion]);

    // reset 'other' region error
    useEffect(() => {
        if (fieldRegion !== 'other') {
            setFieldOtherRegion('');
            setErrors((previous) => {
                return { ...previous, otherRegion: [] };
            });
            setDisabled(false);
        }
    }, [fieldRegion]);

    const handleOnClickSave = () => {
        const errors = validateForm();

        if (
            errors.name.length === 0 &&
            errors.lastName.length === 0 &&
            errors.address.length === 0 &&
            errors.complement.length === 0 &&
            errors.district.length === 0 &&
            errors.municipality.length === 0 &&
            errors.postalCode.length === 0 &&
            errors.region.length === 0 &&
            errors.otherRegion.length === 0
        ) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => NewAddressFormErrors = () => {
        const newErrors: NewAddressFormErrors = {
            general: '',
            name: [],
            lastName: [],
            address: [],
            complement: [],
            district: [],
            municipality: [],
            postalCode: [],
            region: [],
            otherRegion: [],
        };

        !fieldName.trim() && newErrors.name.push(translations.nameRequiredError);
        !fieldLastName.trim() && newErrors.lastName.push(translations.lastNameRequiredError);
        !fieldAddress.trim() && newErrors.address.push(translations.addressRequiredError);
        !fieldPostalCode.trim() && newErrors.postalCode.push(translations.postalCodeRequiredError);
        !fieldRegion && newErrors.region.push(translations.regionRequiredError);

        const visibleDistricts = allGeoDistricts?.data?.filter((district) => district.region_uuid === fieldRegion);
        if (visibleDistricts && visibleDistricts.length > 0) {
            !fieldDistrict.trim() && newErrors.district.push(translations.districtRequiredError);
            !fieldMunicipality.trim() && newErrors.municipality.push(translations.municipalityRequiredError);
        }

        if (fieldRegion === 'other') {
            !fieldOtherRegion.trim() && newErrors.otherRegion.push(translations.otherRegionRequiredError);
        }

        setErrors(newErrors);

        return newErrors;
    };

    // reset postal code errors when changing region
    useEffect(() => {
        setErrors((previous) => {
            return { ...previous, postalCode: [] };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldRegion]);

    const submitForm: () => void = () => {
        const addressData: GetMyAccountAddressesCreateInput = {
            recipient_first_name: fieldName ?? '',
            recipient_last_name: fieldLastName,
            fiscal_number: fieldFiscalNumber,
            type: fieldType,
            address_line1: fieldAddress,
            address_line2: fieldComplement,
            district_code: fieldDistrict,
            municipality_code: fieldMunicipality,
            postal_code: fieldPostalCode,
            region_uuid: fieldRegion !== 'other' ? fieldRegion : undefined,
            other_region: fieldRegion === 'other' ? fieldOtherRegion : undefined,
            primary: fieldPrimary,
        };
        requestGetMyAccountAddressesCreate(addressData);
    };

    const clearForm = () => {
        setFieldName('');
        setFieldLastName('');
        setFieldFiscalNumber('');
        setFieldAddress('');
        setFieldComplement('');
        setFieldDistrict('');
        setFieldMunicipality('');
        setFieldPostalCode('');
        setFieldRegion('');
        setFieldOtherRegion('');
    };

    useEffect(() => {
        if (lastResponseTime) {
            if (errorMessageCreate || validationErrorsCreate) {
                setErrors({
                    general: errorMessageCreate as string,
                    // match form request errors with frontend errors object
                    name: validationErrorsCreate?.recipient_first_name || [],
                    lastName: validationErrorsCreate?.recipient_last_name || [],
                    address: validationErrorsCreate?.address_line1 || [],
                    complement: validationErrorsCreate?.address_line2 || [],
                    district: validationErrorsCreate?.district_code || [],
                    municipality: validationErrorsCreate?.municipality_code || [],
                    postalCode: validationErrorsCreate?.postal_code || [],
                    region: validationErrorsCreate?.region_uuid || [],
                    otherRegion: validationErrorsCreate?.other_region || [],
                });
            } else {
                showDefaultNotification({ type: 'success', message: successMessageCreate || '' });
                requestGetMyAccountAddresses();
                clearForm();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    return (
        <Form formRef={formRef} onSubmit={submitForm}>
            <>
                {errors.general && (
                    <>
                        <ErrorMessages messages={[errors.general]} />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </>
                )}
            </>
            <div className="d-flex flex-column gap-3">
                <div className="row">
                    <div className="col-sm-6 col-12">
                        {/* Name */}
                        <Input
                            type={InputTypes.text}
                            label={translations.nameLabel}
                            variant={InputVariants.labeledInput}
                            value={fieldName}
                            onChange={(value) => handleNameInput(value)}
                            state={errors.name.length > 0 ? InputStates.error : undefined}
                            errorMessages={errors.name}
                        />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </div>
                    <div className="col-sm-6 col-12">
                        {/* last name */}
                        <Input
                            type={InputTypes.text}
                            label={translations.lastNameLabel}
                            variant={InputVariants.labeledInput}
                            value={fieldLastName}
                            onChange={(value) => handleLastNameInput(value)}
                            state={errors.lastName.length > 0 ? InputStates.error : undefined}
                            errorMessages={errors.lastName}
                        />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </div>
                    <div className="col-12">
                        {/* address */}
                        <Input
                            type={InputTypes.text}
                            label={translations.addressLabel}
                            variant={InputVariants.labeledInput}
                            value={fieldAddress}
                            onChange={(value) => setFieldAddress(value)}
                            state={errors.address.length > 0 ? InputStates.error : undefined}
                            errorMessages={errors.address}
                        />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </div>
                    <div className="col-sm-6 col-12">
                        {/* complement */}
                        <Input
                            type={InputTypes.text}
                            label={translations.complementLabel}
                            variant={InputVariants.labeledInput}
                            value={fieldComplement}
                            onChange={(value) => setFieldComplement(value)}
                            state={errors.complement.length > 0 ? InputStates.error : undefined}
                            errorMessages={errors.complement}
                        />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </div>
                    <div className="col-sm-6 col-12">
                        {/* fiscal Number */}
                        <Input
                            type={InputTypes.text}
                            label={translations.fiscalNumberLabel}
                            variant={InputVariants.labeledInput}
                            value={fieldFiscalNumber}
                            onChange={(value) => handleFiscalInput(value)}
                            maxLength={9}
                        />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </div>
                    <div className="col-sm-6 col-12 pt-sm-2 mt-sm-1">
                        {/* postalCode */}
                        <Input
                            type={InputTypes.text}
                            label={translations.postalCodeLabel}
                            variant={InputVariants.labeledInput}
                            value={fieldPostalCode}
                            onChange={(value) => setFieldPostalCode(value)}
                            maxLength={8}
                            state={errors.postalCode.length > 0 ? InputStates.error : undefined}
                            errorMessages={errors.postalCode}
                        />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </div>
                    <div className="col-sm-6 col-12">
                        {/* region */}
                        <Typography color="muted" fontSize="12">
                            {translations.regionLabel}
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            options={[
                                { value: '', text: translations.default },
                                ...(allSettings?.data?.regions || []).map((region) => ({
                                    text: region.name || '',
                                    value: region.uuid || '',
                                })),
                                { value: 'other', text: translations.regionOtherOption },
                            ]}
                            value={fieldRegion}
                            onChange={(value) => setFieldRegion(value)}
                        />
                        {errors.region.length > 0 && <ErrorMessages messages={errors.region} />}
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </div>
                    {fieldRegion === 'other' && (
                        <div className="col-12">
                            {/* other region */}
                            <Input
                                type={InputTypes.text}
                                label={translations.otherRegion}
                                variant={InputVariants.labeledInput}
                                value={fieldOtherRegion}
                                onChange={(value) => setFieldOtherRegion(value)}
                                state={errors.otherRegion.length > 0 ? InputStates.error : undefined}
                                errorMessages={errors.otherRegion}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                    )}
                    {/* city / district */}
                    <div className="col-sm-6 col-12">
                        <Typography color="muted" fontSize="12">
                            {translations.districtLabel}
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            options={[
                                { text: translations.selectDistrict, value: '' },
                                ...filteredDistricts.map((district) => ({
                                    text: district.name || '',
                                    value: district.district_code || '',
                                })),
                            ]}
                            value={fieldDistrict}
                            onChange={(value) => setFieldDistrict(value)}
                            disabled={districtAndMunicipalityDisabled || loadingGetAllGeoDistricts}
                        />
                        {errors.district.length > 0 && <ErrorMessages messages={errors.district} />}
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </div>
                    {/* municipality */}
                    <div className="col-sm-6 col-12">
                        <MunicipalitiesSelect
                            fieldMunicipality={fieldMunicipality}
                            setFieldMunicipality={(value: string) => setFieldMunicipality(value)}
                            disabled={districtAndMunicipalityDisabled || loadingGetAllGeoMunicipalities}
                            errors={errors.municipality}
                        />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </div>
                </div>
                <Button
                    label={translations.title}
                    classes="w-100"
                    mode={ButtonMode.primary}
                    onClick={handleOnClickSave}
                    disabled={disabled}
                />
            </div>
        </Form>
    );
};

interface MunicipalitiesSelectProps {
    fieldMunicipality: string;
    setFieldMunicipality: (value: string) => void;
    disabled: boolean;
    errors: NewAddressFormErrors['municipality'];
}

const MunicipalitiesSelect: FC<MunicipalitiesSelectProps> = ({ fieldMunicipality, setFieldMunicipality, disabled, errors }) => {
    const { allGeoMunicipalitiesOfDistrict } = useData();
    const { t } = useTranslation();

    const translations = {
        municipalityLabel: t('new-address-form-component.municipality.label'),
        selectMunicipality: t('new-address-form-component.municipality.select'),
    };

    return (
        <>
            <Typography color="muted" fontSize="12">
                {translations.municipalityLabel}
            </Typography>
            <Select
                classes="w-100"
                type={SelectType.variant1}
                options={[
                    { text: translations.selectMunicipality, value: '' },
                    ...(allGeoMunicipalitiesOfDistrict?.data?.map((municipality) => ({
                        text: municipality.name ?? '',
                        value: municipality.municipality_code ?? '',
                    })) || []),
                ]}
                value={fieldMunicipality}
                onChange={(value) => setFieldMunicipality(value)}
                disabled={disabled}
            />
            {errors.length > 0 && <ErrorMessages messages={errors} />}
        </>
    );
};
